import { Link, useSearchParams, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { MapPin, Sparkles, Store, Palette, Music, Camera, ArrowRight, Shield, Navigation as NavigationIcon, Coffee, ShoppingBag, Heart, Landmark, Wheat } from "lucide-react";

/**
 * Public landing page shown to anyone without an account.
 * First impression before they sign up — matches the pitch tone in the
 * founder pitch plan (confident & professional).
 */
export default function Welcome() {
    const { user, loading } = useAuth();
    const [params] = useSearchParams();
    const next = params.get("next") || "/";

    // If they're already signed in, skip straight through.
    if (!loading && user) {
        return <Navigate to={next} replace />;
    }

    const authHref = `/auth?next=${encodeURIComponent(next)}`;

    return (
        <div className="fade-up" data-testid="welcome-page">
            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 -z-10 bg-gradient-to-br from-stone-50 via-background to-stone-100" />
                <div className="absolute inset-0 -z-10 opacity-[0.04]" style={{
                    backgroundImage: "radial-gradient(circle at 20% 30%, hsl(131 22% 29%) 0, transparent 50%), radial-gradient(circle at 80% 70%, hsl(13 69% 62%) 0, transparent 50%)",
                }} />
                <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16 md:py-24">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest">
                            <Sparkles size={12} /> Members-only, always local
                        </div>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
                            A map of the makers,<br />shops &amp; gigs <span className="text-primary">near you</span>.
                        </h1>
                        <p className="mt-6 text-lg sm:text-xl text-foreground/80 max-w-2xl leading-relaxed">
                            From Here is a members-only marketplace built around one idea: everything you see is within walking distance.
                            No dropshippers. No algorithm. No noise. Just the people around you.
                        </p>

                        <div className="mt-9 flex flex-wrap gap-3">
                            <Link to={`${authHref}&tab=register`}>
                                <Button size="lg" data-testid="welcome-signup-btn"
                                    className="rounded-full h-12 px-6 bg-primary hover:bg-primary/90 hover:text-white text-base font-bold shadow-lg">
                                    Create your account <ArrowRight size={16} className="ml-1.5" />
                                </Button>
                            </Link>
                            <Link to={authHref}>
                                <Button size="lg" variant="outline" data-testid="welcome-signin-btn"
                                    className="rounded-full h-12 px-6 text-base font-bold border-2">
                                    I have an account · Sign in
                                </Button>
                            </Link>
                        </div>
                        <p className="mt-4 text-sm text-muted-foreground">
                            No invite code yet? <Link to={`/join${window.location.search}`} className="font-bold text-primary underline inline-block py-2" data-testid="welcome-early-access">Get early access →</Link>
                        </p>

                        <p className="mt-4 text-xs text-muted-foreground">
                            Free forever · No card needed · Takes about 30 seconds
                        </p>
                    </div>
                </div>
            </section>

            {/* What's on the map */}
            <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-16">
                <div className="text-xs uppercase tracking-[0.25em] font-bold text-muted-foreground mb-3">Eight layers on one map</div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="welcome-layers">
                    {[
                        { Icon: Coffee,      name: "Eat & Drink",          desc: "Cafés, bakeries, restaurants, bars, delis — the flavour of your street." },
                        { Icon: ShoppingBag, name: "Shops",                 desc: "Independent retail, bookshops, record shops. Places to browse and buy." },
                        { Icon: Heart,       name: "Services & Wellness", desc: "Salons, gyms, coworking, stays — the everyday stuff that keeps a neighbourhood ticking." },
                        { Icon: Landmark,    name: "Venues & Spaces",     desc: "Theatres, music venues, galleries, halls, museums — the places that host what's on." },
                        { Icon: Palette,     name: "Makers & Artists",     desc: "Ceramics, prints, jewellery, textiles — from studios near you." },
                        { Icon: MapPin,      name: "Markets & events",     desc: "Fairs, pop-ups, open studios, workshops — time-limited things." },
                        { Icon: Music,       name: "Gigs & shows",         desc: "Music, comedy, quiz nights — tickets sold, scanned at the door." },
                        { Icon: Wheat,       name: "Farms & Producers",     desc: "Farms, dairies, growers, breweries and roasters, and the shops and kitchens they supply." },
                    ].map(({ Icon, name, desc }) => (
                        <div key={name} className="p-4 rounded-2xl border border-stone-200 bg-card hover:border-primary/40 hover:-translate-y-0.5 transition-all">
                            <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                                <Icon size={16} />
                            </div>
                            <div className="font-bold text-sm">{name}</div>
                            <div className="text-[12px] text-muted-foreground leading-snug mt-1">{desc}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Why sign up */}
            <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-20">
                <div className="grid gap-6 md:grid-cols-3">
                    <div className="p-6 rounded-2xl border border-stone-200 bg-card">
                        <NavigationIcon className="text-primary mb-3" size={20} />
                        <div className="font-bold text-lg">Everything is walking distance</div>
                        <p className="text-sm text-muted-foreground leading-relaxed mt-2">
                            Set a home pin, choose a radius. Every listing on your map is genuinely near — nothing further, nothing fake.
                        </p>
                    </div>
                    <div className="p-6 rounded-2xl border border-stone-200 bg-card">
                        <Shield className="text-primary mb-3" size={20} />
                        <div className="font-bold text-lg">Real people, real places</div>
                        <p className="text-sm text-muted-foreground leading-relaxed mt-2">
                            Members only, no dropshippers, no reselling. Makers can use a fuzzy pin so their home address stays private.
                        </p>
                    </div>
                    <div className="p-6 rounded-2xl border border-stone-200 bg-card">
                        <Store className="text-primary mb-3" size={20} />
                        <div className="font-bold text-lg">Sell what you make</div>
                        <p className="text-sm text-muted-foreground leading-relaxed mt-2">
                            Your free pin is a mini-shop. Keep 91% of every sale — flat 9% all-in, no cap, Stripe fees absorbed. Payouts to your own bank.
                        </p>
                    </div>
                </div>
            </section>

            {/* CTA footer */}
            <section className="bg-primary text-primary-foreground">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                        <div className="font-display text-3xl md:text-4xl font-black tracking-tighter">Come inside — pick your pin.</div>
                        <p className="text-sm opacity-90 mt-2 max-w-xl">
                            Create an account to unlock the map, save the places you love, and (if you want) put your own pin on it.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Link to={`${authHref}&tab=register`}>
                            <Button size="lg" data-testid="welcome-cta-signup"
                                className="rounded-full h-12 px-6 bg-white text-primary hover:bg-white/90 text-base font-bold">
                                Create your account <ArrowRight size={16} className="ml-1.5" />
                            </Button>
                        </Link>
                        <Link to={authHref}>
                            <Button size="lg" variant="outline" data-testid="welcome-cta-signin"
                                className="rounded-full h-12 px-6 border-2 border-white/60 bg-transparent text-white hover:bg-white/10 hover:text-white text-base font-bold">
                                Sign in
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Legal footer strip */}
            <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 text-xs text-muted-foreground flex flex-wrap gap-x-6 gap-y-2">
                <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
                <Link to="/terms" className="hover:text-foreground">Terms</Link>
                <Link to="/community-guidelines" className="hover:text-foreground">Community guidelines</Link>
                <span className="ml-auto">© From Here — a marketplace for the people around you.</span>
            </div>
        </div>
    );
}
